import { Injectable, Type, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AwareHttpService } from '@appbolaget/aware-http';
import { File } from '@appbolaget/aware-model';
import { Store } from '@ngxs/store';
import { UnitState } from 'app/state/unit.state';

export interface MediaDialogConfigOptions {
    multiple?: boolean;
    showDefaultLibrary?: boolean;
    showMyLibrary?: boolean;
    canCreateBucket?: boolean;
    canUploadFile?: boolean;
    rules?: any;
}
export interface MediaDialogConfig {
    expectsValue?: boolean;
    options?: MediaDialogConfigOptions;
}
@Injectable()
export class MediaDialogService {
    #mediaDialogComponent: Promise<Type<any>>;
    dialogRef: MatDialogRef<any, File | File[]>;
    defaultOptions: MediaDialogConfig['options'] = {
        multiple: true,
        showDefaultLibrary: true,
        showMyLibrary: false,
        canCreateBucket: true,
        canUploadFile: true,
        rules: null,
    };

    currentOptions;

    private store = inject(Store);
    constructor(
        public dialogService: MatDialog,
        public api: AwareHttpService,
    ) {}

    async open(expectsValue: boolean = false, options?: {}) {
        this.currentOptions = this.getOptions(options);

        if (!this.#mediaDialogComponent) {
            this.#mediaDialogComponent = import('./media-dialog.component').then(({ MediaDialogComponent }) => MediaDialogComponent);
        }

        return this.#mediaDialogComponent.then((comp) => {
            this.dialogRef = this.dialogService.open<any, MediaDialogConfig, File | File[]>(comp, {
                panelClass: 'Media-dialog__overlay',
                maxWidth: '100%',
                maxHeight: '100%',
                width: '80%',
                height: '80%',
                data: {
                    expectsValue: expectsValue,
                    options: this.currentOptions,
                },
            });
            return this.dialogRef;
        });
    }

    canReturnSelectedFiles(files: File[]): boolean {
        const rules = this.currentOptions.rules;
        let canReturn = true;

        if (!rules) {
            return canReturn;
        }

        for (const file of <File[]>files) {
            if (!file.isImage()) {
                continue;
            }

            file.deleteMetaByKey('rule-violation');

            const width = parseInt(file.getMetaFieldValue('width'), 10);
            const height = parseInt(file.getMetaFieldValue('height'), 10);

            if (rules.minWidth && width < rules.minWidth) {
                file.createMeta('rule-violation', `Bilden måste vara minst <b>${rules.minWidth}px</b> bred`);
                canReturn = false;
            }

            if (rules.minHeight && height < rules.minHeight) {
                file.createMeta('rule-violation', `Bilden måste vara minst <b>${rules.minHeight}px</b> hög`);
                canReturn = false;
            }
        }

        return canReturn;
    }

    getOptions(options?: MediaDialogConfigOptions): MediaDialogConfigOptions {
        return Object.assign({ mainUnit: this.store.selectSnapshot(UnitState.activeUnit) }, this.defaultOptions, options);
    }

    returnFiles(files: File[]) {
        const returnValue = this.currentOptions.multiple ? files : files[0];

        this.dialogRef.close(returnValue);
    }
}
